import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Circle, Clock, LogOut, FileText, Plane, MessageSquare } from "lucide-react";
import { http } from "@/lib/api";
import { clearPortalSession, portalHttp } from "@/lib/portalAuth";

const STAGES = [
  { key: "profile",    label: "Profile & intake",            Icon: FileText,     done: true },
  { key: "docs",       label: "Document collection",         Icon: FileText,     done: true,  active: false },
  { key: "review",     label: "Advisor case review",         Icon: MessageSquare, done: false, active: true },
  { key: "submitted",  label: "Application submitted",       Icon: Plane,        done: false },
  { key: "decision",   label: "Decision received",           Icon: CheckCircle2, done: false },
];

export default function ApplicantDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalHttp.get("/portal/me")
      .then(({ data }) => setMe(data))
      .catch((err) => {
        console.error("Portal /me failed:", err);
        nav("/applicant/login");
      })
      .finally(() => setLoading(false));
  }, [nav]);

  const logout = async () => {
    try { await http.post("/portal/logout"); } catch (e) {}
    clearPortalSession();
    toast.success("Signed out");
    nav("/applicant/login");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!me) return null;

  return (
    <div data-testid="applicant-dashboard" className="min-h-screen bg-[#F4F6F9] text-[#0F172A]">
      <header className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="NS-I" className="h-9 w-auto" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">Applicant portal</div>
            <div className="font-serif text-lg">Hello, {me.name || me.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {me.picture && <img src={me.picture} alt="" className="w-8 h-8 rounded-full" />}
          <button data-testid="applicant-logout" onClick={logout} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#C9A961]">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <section className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-6">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Current case</div>
          <h2 className="font-serif text-2xl">Work visa · Canada</h2>
          <div className="mt-2 inline-flex items-center gap-1.5 text-xs rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1">
            <Clock className="w-3 h-3" /> ETA 8–12 weeks
          </div>
        </section>

        <section className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-5">Milestones</div>
          <ol className="space-y-5">
            {STAGES.map((s) => (
              <li key={s.key} data-testid={`milestone-${s.key}`} className="flex items-start gap-4">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${s.done ? "bg-emerald-100 text-emerald-700" : s.active ? "bg-[#C9A961]/20 text-[#8B6F30]" : "bg-slate-100 text-slate-400"}`}>
                  {s.done ? <CheckCircle2 className="w-5 h-5" /> : s.active ? <Clock className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                </div>
                <div className="flex-1">
                  <div className={`text-sm font-semibold ${s.active ? "text-[#0B1E3F]" : s.done ? "text-slate-700" : "text-slate-400"}`}>{s.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {s.done ? "Completed" : s.active ? "In progress — your advisor is reviewing" : "Pending"}
                  </div>
                </div>
                <s.Icon className={`w-4 h-4 ${s.active ? "text-[#C9A961]" : "text-slate-300"}`} />
              </li>
            ))}
          </ol>
          <p className="mt-6 text-[11px] text-slate-400">Demo data · live timeline sync coming soon.</p>
        </section>
      </main>
    </div>
  );
}
